import React, { useEffect, useMemo, useState } from "react";
import { LassoSimpleButtonFull } from "../../components/ui/utils/LassoButtons";
import TopMostPortal from "../../components/ui/utils/Portals";
import RestAPI from "../../utils/restAPI";
import { logError } from "../../shared/common/logger";
import { toastNotify } from "../../shared/client/FrontendUtils";
import CRMIntegrationCard from "./CRMIntegrationsModal/CRMIntegrationCard";
import {
	CRM_INTEGRATION_LABELS,
	CRMIntegration,
	CRMIntegrationType,
	defaultsForType,
	duplicateValueError,
	duplicateEnabledTypes,
	lockedKeysFor,
	normalizeFromServer,
	serializeIntegration,
	typeOrdinals,
	validateIntegration,
} from "./CRMIntegrationsModal/crmIntegrationTypes";

interface CRMIntegrationsModalProps {
	organizationId: string;
	organizationName: string;
	onClose: () => void;
}


interface EditorRow {
	key: string;
	integration: CRMIntegration;
	/** Keys this row loaded from the server holding, which must not be edited. */
	lockedKeys: readonly string[];
}

let rowCounter = 0;
const nextRowKey = (): string => `crm-row-${rowCounter++}`;

const CRMIntegrationsModal: React.FC<CRMIntegrationsModalProps> = ({ organizationId, organizationName, onClose }) => {
	const [rows, setRows] = useState<EditorRow[]>([]);
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [saving, setSaving] = useState(false);
	const [choosingType, setChoosingType] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const fetchIntegrations = async () => {
			setLoading(true);
			setLoadFailed(false);
			try {
				const response = await RestAPI.fetchAPI(
					`/rest/access/organization/${organizationId}/crmIntegrations`,
					"GET"
				);
				if (cancelled) {
					return;
				}
				if (!Array.isArray(response)) {
					logError("CRMIntegrationsModal: failed to load crmIntegrations", response);
					setLoadFailed(true);
					return;
				}
				const loaded = (response as CRMIntegration[]).map((entry) => {
					const { integration, unknownKeys } = normalizeFromServer(entry);
					if (unknownKeys.length > 0) {
						logError("CRMIntegrationsModal: dropping unrecognized field(s) on", entry.type, unknownKeys);
					}
					return { key: nextRowKey(), integration, lockedKeys: lockedKeysFor(integration.type) };
				});
				setRows(loaded);
				setExpandedKeys(new Set(loaded.length === 1 ? [loaded[0].key] : []));
			} catch (err) {
				if (!cancelled) {
					logError("CRMIntegrationsModal: error loading crmIntegrations", err);
					setLoadFailed(true);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchIntegrations();
		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	const integrations = useMemo(() => rows.map((row) => row.integration), [rows]);
	const duplicates = useMemo(() => duplicateEnabledTypes(integrations), [integrations]);
	const ordinals = useMemo(() => typeOrdinals(integrations), [integrations]);
	const issues = useMemo(
		() => integrations.map((integration, index) => validateIntegration(integration, ordinals[index])),
		[integrations, ordinals]
	);
	const validationError = issues.find((issue) => issue !== null) ?? duplicateValueError(integrations);

	const addIntegration = (type: CRMIntegrationType) => {
		// Nothing is locked on a row the admin is creating.
		const row: EditorRow = { key: nextRowKey(), integration: defaultsForType(type), lockedKeys: [] };
		setRows((prev) => [...prev, row]);
		setExpandedKeys((prev) => new Set([...prev, row.key]));
		setChoosingType(false);
	};

	const updateRow = (key: string, integration: CRMIntegration) => {
		setRows((prev) => prev.map((row) => (row.key === key ? { ...row, integration } : row)));
	};

	const removeRow = (key: string) => {
		setRows((prev) => prev.filter((row) => row.key !== key));
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			next.delete(key);
			return next;
		});
	};

	const toggleExpanded = (key: string) => {
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const handleSave = async () => {
		if (loadFailed || validationError) {
			return; // the button is already disabled; belt and braces
		}
		setSaving(true);
		try {
			const crmIntegrations = integrations.map(serializeIntegration);
			const response = await RestAPI.fetchAPI(
				`/rest/access/organization/${organizationId}/crmIntegrations`,
				"PUT",
				{ crmIntegrations }
			);
			if (!response) {
				logError("CRMIntegrationsModal: save rejected", response);
				toastNotify("Failed to save CRM integrations — see the browser console for the reason", true);
				return;
			}
			toastNotify("CRM integrations saved", false);
			onClose();
		} catch (err) {
			logError("CRMIntegrationsModal: error saving crmIntegrations", err);
			toastNotify("Failed to save CRM integrations", true);
		} finally {
			setSaving(false);
		}
	};

	const renderBody = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center h-64">
					<div className="text-gray-600">Loading CRM integrations...</div>
				</div>
			);
		}

		if (loadFailed) {
			return (
				<div className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm">
					Could not load CRM integrations for this organization. Saving is disabled so the existing
					configuration is not overwritten. Close and try again — the reason is in the browser console.
				</div>
			);
		}

		return (
			<div className="space-y-4">
				{duplicates.length > 0 && (
					<div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
						More than one <b>enabled</b> entry for: {duplicates.map((type) => CRM_INTEGRATION_LABELS[type]).join(", ")}.
						Only the first enabled entry of each type is used by the backend today.
					</div>
				)}

				<div className="flex justify-between items-center">
					<div className="text-gray-900 text-base font-medium font-['Hanken Grotesk']">
						Integrations for {organizationName}
					</div>
					<button
						type="button"
						onClick={() => setChoosingType((open) => !open)}
						className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
					>
						+ Add Integration
					</button>
				</div>

				{choosingType && (
					<div className="p-4 bg-white rounded-lg border border-gray-200 space-y-3">
						<div className="flex justify-between items-center">
							<div className="text-gray-700 text-sm font-medium">Choose an integration type</div>
							<button
								type="button"
								onClick={() => setChoosingType(false)}
								className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
							>
								Cancel
							</button>
						</div>
						<div className="flex flex-wrap gap-2">
							{Object.values(CRMIntegrationType).map((type) => (
								<button
									key={type}
									type="button"
									onClick={() => addIntegration(type)}
									className="px-4 py-2.5 bg-[#eff8ff] rounded-lg border border-[#eff8ff] hover:border-[#156fee] text-[#156fee] text-sm font-semibold font-['Hanken Grotesk']"
								>
									{CRM_INTEGRATION_LABELS[type]}
								</button>
							))}
						</div>
					</div>
				)}

				{rows.length === 0 && !choosingType ? (
					<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
						No CRM integrations configured. Click "Add Integration" to create one.
					</div>
				) : (
					rows.map((row, index) => (
						<CRMIntegrationCard
							key={row.key}
							integration={row.integration}
							lockedKeys={row.lockedKeys}
							position={ordinals[index]}
							expanded={expandedKeys.has(row.key)}
							issue={issues[index]}
							onToggleExpand={() => toggleExpanded(row.key)}
							onChange={(next) => updateRow(row.key, next)}
							onRemove={() => removeRow(row.key)}
						/>
					))
				)}
			</div>
		);
	};

	return (
		<TopMostPortal>
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className="bg-white w-[900px] max-h-[90vh] rounded-xl shadow-lg flex flex-col">
					<div className="flex flex-shrink-0 w-full h-12 bg-gray-100 rounded-t-xl justify-between items-center px-4 border-b border-gray-200">
						<div className="text-gray-900 text-lg font-semibold font-['Inter'] leading-7 m-4">
							CRM Integrations
						</div>
						<button onClick={onClose} className="cursor-pointer p-1 hover:bg-gray-200 rounded">
							<img className="w-5 h-5" src="/assets/ui/x.svg" alt="Close" />
						</button>
					</div>

					<div className="flex-1 overflow-y-auto p-6 bg-gray-50">{renderBody()}</div>

					<div className="w-full h-20 flex-shrink-0 bg-white border-t border-gray-200 flex items-center justify-end gap-4 px-6 rounded-b-xl">
						{validationError && !loading && !loadFailed && (
							<div className="text-sm text-red-600 mr-auto">{validationError}</div>
						)}
						<LassoSimpleButtonFull
							caption="Cancel"
							onClick={onClose}
							customButtonStyle={{ backgroundColor: "white", border: "1px solid #EAECF0" }}
							customTextStyle={{ color: "#344054" }}
							disabled={saving}
						/>
						<LassoSimpleButtonFull
							caption={saving ? "Saving..." : "Save"}
							onClick={handleSave}
							disabled={saving || loading || loadFailed || validationError !== null}
						/>
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};

export default CRMIntegrationsModal;
